import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = ["pending", "active", "complete"]; 

const PromoCampaignQueue = ({ user }) => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [campaigns, setCampaigns] = useState([]);
  const [selected, setSelected] = useState(null);

  const canAccess = useMemo(
    () => ["owner", "co_ceo", "treasurer", "director"].includes(user?.role),
    [user?.role]
  );

  useEffect(() => {
    if (!canAccess) return;

    const fetchCampaigns = async () => {
      setLoading(true);
      try {
        const url =
          statusFilter === "all"
            ? `${API}/admin/promo/campaigns`
            : `${API}/admin/promo/campaigns?status=${statusFilter}`;
        const resp = await fetch(url, { credentials: "include" });
        if (!resp.ok) {
          toast.error("Failed to load campaigns");
          setCampaigns([]);
          setLoading(false);
          return;
        }
        const data = await resp.json();
        setCampaigns(data?.campaigns ?? data ?? []);
      } catch (e) {
        toast.error("Failed to load campaigns");
        setCampaigns([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [statusFilter, canAccess]);

  const updateStatus = async () => {
    if (!selected) return;
    try {
      const resp = await fetch(`${API}/admin/promo/campaigns/${selected.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: selected.status, internal_notes: selected.internal_notes || "" }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || "Update failed");
        return;
      }

      toast.success("Updated");
      // Refresh list (best-effort)
      window.location.reload();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  if (!canAccess) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#FAFAFA]">Promo Campaigns</h1>
          <p className="text-[#A1A1AA] mt-1">Access denied.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="promo-campaign-queue">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#FAFAFA]">Promo Campaign Queue</h1>
          <p className="text-[#A1A1AA] mt-1">Review submissions, set status, and add internal notes.</p>
        </div>
        <Button className="btn-gold" onClick={() => navigate("/promo")}>Open Portal</Button>
      </div>

      <Card className="bg-[#121214] border-[#27272A]">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <Label className="text-[#A1A1AA]">Status</Label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-[#1A1A1D] border border-[#27272A] text-[#FAFAFA] rounded-md px-3 py-2"
              data-testid="promo-status-filter"
            >
              <option value="all">All</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 bg-[#121214] border-[#27272A]">
          <CardContent className="pt-6">
            <div className="space-y-3">
              {campaigns.map((c) => (
                <button
                  key={c.id}
                  className={`w-full text-left rounded-xl border px-4 py-3 transition-all ${
                    selected?.id === c.id ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#27272A] hover:border-[#D4AF37]/60"
                  }`}
                  onClick={() => setSelected(c)}
                  data-testid={`promo-campaign-${c.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-[#FAFAFA] font-semibold">{c.artist_name} — {c.title}</p>
                      <p className="text-xs text-[#71717A] mt-1">{c.package_name} · ${c.amount} · {c.duration_days} days</p>
                    </div>
                    <span className="text-xs text-[#A1A1AA] capitalize">{c.status}</span>
                  </div>
                </button>
              ))}

              {campaigns.length === 0 && (
                <div className="text-center py-10 text-sm text-[#A1A1AA]">No campaigns found.</div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#121214] border-[#27272A]">
          <CardContent className="pt-6">
            {!selected ? (
              <p className="text-sm text-[#A1A1AA]">Select a campaign to review.</p>
            ) : (
              <>
                <h3 className="text-sm font-semibold text-[#FAFAFA]">Update Campaign</h3>
                <div className="mt-4">
                  <Label className="text-[#A1A1AA]">Status</Label>
                  <select
                    value={selected.status}
                    onChange={(e) => setSelected((p) => ({ ...p, status: e.target.value }))}
                    className="mt-1 w-full bg-[#1A1A1D] border border-[#27272A] text-[#FAFAFA] rounded-md px-3 py-2"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div className="mt-4">
                  <Label className="text-[#A1A1AA]">Internal notes</Label>
                  <Textarea
                    value={selected.internal_notes || ""}
                    onChange={(e) => setSelected((p) => ({ ...p, internal_notes: e.target.value }))}
                    className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    rows={5}
                  />
                </div>

                <Button className="w-full btn-gold mt-4" onClick={updateStatus} data-testid="promo-update-btn">Save</Button>

                <div className="mt-6">
                  <Label className="text-[#A1A1AA]">Purchaser</Label>
                  <Input value={`${selected.email || ""}`} readOnly className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                  <Input value={`${selected.phone || ""}`} readOnly className="mt-2 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]" />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PromoCampaignQueue;
